import {StorageWrapper} from '../../../shared/scripts/StorageWrapper'
import {ViewPopup} from '../../../shared/scripts/ui_popup/ViewPopup'
import {UIMDeviceHandler} from '../ui_html/UIMDeviceHandler'
import {TimeUtils} from '@colonist/utils'
import {AnalyticEventCategory, AnalyticEventType} from '../../../../shared/entity_datas/EntityDatas'
import {CA} from '../../../shared/scripts/analytics/Analytics'
import {TranslationObject} from '@colonist/i18n'
import {ClientUserState} from '../../../shared/scripts/ClientUserState'

const USER_ACTION_ENUM = {
    USER_REJECTED_PUSH_NOTIFICATION: 0,
    USER_ACCEPTED_PUSH_NOTIFICATION: 1,
}

export enum PushNotificationPopupIntentEnum {
    Default = 'DEFAULT',
    OnLoadHomePage = 'ON_LOAD_HOME_PAGE',
    OnUserClosesPatchNotesScreen = 'ON_USER_CLOSES_PATCH_NOTES_SCREEN',
    OnUserSentFriendRequest = 'ON_USER_SENT_FRIEND_REQUEST',
    OnUserBringAppToForeground = 'ON_USER_BRING_APP_TO_FOREGROUND',
}

export interface PushNotificationPopupType {
    intent: PushNotificationPopupIntentEnum
    frequency: number
    title: TranslationObject
    body: TranslationObject
    checkButtonCustomContent: TranslationObject
    xButtonCustomContent: TranslationObject
}

export const pushNotificationPopupData: PushNotificationPopupType[] = [
    {
        intent: PushNotificationPopupIntentEnum.Default,
        frequency: TimeUtils.MILLISECONDS * TimeUtils.SECONDS_IN_MINUTE * TimeUtils.MINUTES_IN_HOUR * TimeUtils.HOURS_IN_DAY * TimeUtils.DAYS_IN_WEEK,
        title: {key: 'strings:homePage.popups.requestPushNotification.title'},
        body: {key: 'strings:homePage.popups.requestPushNotification.body'},
        checkButtonCustomContent: {key: 'strings:homePage.popups.requestPushNotification.checkButtonCustomContent'},
        xButtonCustomContent: {key: 'strings:homePage.popups.requestPushNotification.xButtonCustomContent'},
    },
    {
        intent: PushNotificationPopupIntentEnum.OnLoadHomePage,
        frequency: TimeUtils.MILLISECONDS * TimeUtils.SECONDS_IN_MINUTE * TimeUtils.MINUTES_IN_HOUR * TimeUtils.HOURS_IN_DAY * TimeUtils.DAYS_IN_WEEK,
        title: {key: 'strings:homePage.popups.requestPushNotification.title'},
        body: {key: 'strings:homePage.popups.requestPushNotification.body'},
        checkButtonCustomContent: {key: 'strings:homePage.popups.requestPushNotification.checkButtonCustomContent'},
        xButtonCustomContent: {key: 'strings:homePage.popups.requestPushNotification.xButtonCustomContent'},
    },
    {
        intent: PushNotificationPopupIntentEnum.OnUserClosesPatchNotesScreen,
        frequency: TimeUtils.MILLISECONDS * TimeUtils.SECONDS_IN_MINUTE * TimeUtils.MINUTES_IN_HOUR * TimeUtils.HOURS_IN_DAY,
        title: {key: 'strings:homePage.popups.requestPushNotificationOnPatchNotesScreen.title'},
        body: {key: 'strings:homePage.popups.requestPushNotificationOnPatchNotesScreen.body'},
        checkButtonCustomContent: {key: 'strings:homePage.popups.requestPushNotificationOnPatchNotesScreen.checkButtonCustomContent'},
        xButtonCustomContent: {key: 'strings:homePage.popups.requestPushNotificationOnPatchNotesScreen.xButtonCustomContent'},
    },
    {
        intent: PushNotificationPopupIntentEnum.OnUserSentFriendRequest,
        frequency: TimeUtils.MILLISECONDS * TimeUtils.SECONDS_IN_MINUTE * TimeUtils.MINUTES_IN_HOUR * TimeUtils.HOURS_IN_DAY,
        title: {key: 'strings:homePage.popups.requestPushNotificationOnFriendRequest.title'},
        body: {key: 'strings:homePage.popups.requestPushNotificationOnFriendRequest.body'},
        checkButtonCustomContent: {key: 'strings:homePage.popups.requestPushNotificationOnFriendRequest.checkButtonCustomContent'},
        xButtonCustomContent: {key: 'strings:homePage.popups.requestPushNotificationOnFriendRequest.xButtonCustomContent'},
    },
    {
        intent: PushNotificationPopupIntentEnum.OnUserBringAppToForeground,
        frequency: TimeUtils.MILLISECONDS * TimeUtils.SECONDS_IN_MINUTE * TimeUtils.MINUTES_IN_HOUR * TimeUtils.HOURS_IN_DAY * TimeUtils.DAYS_IN_WEEK,
        title: {key: 'strings:homePage.popups.requestPushNotificationOnBringAppToForeground.title'},
        body: {key: 'strings:homePage.popups.requestPushNotificationOnBringAppToForeground.body'},
        checkButtonCustomContent: {key: 'strings:homePage.popups.requestPushNotificationOnBringAppToForeground.checkButtonCustomContent'},
        xButtonCustomContent: {key: 'strings:homePage.popups.requestPushNotificationOnBringAppToForeground.xButtonCustomContent'},
    },
]

export class UIMRequestPushNotification extends ViewPopup {
    private readonly popupType: PushNotificationPopupType

    constructor(intent: PushNotificationPopupIntentEnum) {
        const popupType = pushNotificationPopupData.find((popup) => popup.intent === intent)
        if(!popupType) {
            throw new Error('Popup type not found')
        }

        super(popupType.title,
            popupType.body,
            () => {
                UIMDeviceHandler.requestNotificationPermission()
                void CA.sendAnalyticEvent({
                    type: AnalyticEventType.ABTestClickAnalytics,
                    category: AnalyticEventCategory.ABTestPushNotificationsPopup,
                    floatValue: USER_ACTION_ENUM.USER_ACCEPTED_PUSH_NOTIFICATION,
                    stringValue: this.popupType.intent,
                })
                StorageWrapper.setPushNotificationRequestStatus(true)
            },
            () => {
                void CA.sendAnalyticEvent({
                    type: AnalyticEventType.ABTestClickAnalytics,
                    category: AnalyticEventCategory.ABTestPushNotificationsPopup,
                    floatValue: USER_ACTION_ENUM.USER_REJECTED_PUSH_NOTIFICATION,
                    stringValue: this.popupType.intent,
                })
            },
            false,
            {
                checkButtonCustomContent: popupType.checkButtonCustomContent,
                xButtonCustomContent: popupType.xButtonCustomContent,
            })
        this.popupType = popupType

        const shouldRequestPushNotification = this.shouldRequestPushNotification()
        if(!shouldRequestPushNotification) {
            this.removePopup()
            return
        }

        const timestampData = StorageWrapper.getRequestPushNotificationTimeData()
        const newTimestampData = {
            ...timestampData,
            [this.popupType.intent]: new Date(),
        }
        StorageWrapper.setRequestPushNotificationTimeData(newTimestampData)

        this.stylePopup()
        this.show()
    }

    private stylePopup() {
        this.body.classList.add('request-push-notification-body')
        this.xButton.classList.add('btn_ghost')
        this.xButton.style.maxWidth = 'unset'
        this.checkButton.style.maxWidth = 'unset'
    }

    private shouldRequestPushNotification(): boolean {
        const hasUserBypassedLogin = StorageWrapper.getIfUserBypassedLogin()
        const isUserLoggedIn = ClientUserState.userState?.isLoggedIn
        if(!hasUserBypassedLogin && !isUserLoggedIn) return false

        const hasUserAcceptedPushNotification = StorageWrapper.getPushNotificationRequestStatus()
        if(hasUserAcceptedPushNotification) return false

        const hasUserBeenPromptedRecentlyForSameIntent = this.hasUserBeenPromptedRecentlyForSameIntent()
        if(hasUserBeenPromptedRecentlyForSameIntent) return false

        const hasUserBeenPromptedRecently = this.hasUserBeenPromptedRecently()
        if(hasUserBeenPromptedRecently) return false

        return true
    }

    // eslint-disable-next-line class-methods-use-this
    private getLatestPopupTimestamp(): number {
        const allTimestamps = Object.values(StorageWrapper.getRequestPushNotificationTimeData())
            .filter((date) => date != null)
            .map((date) => new Date(date).valueOf())

        if(allTimestamps.length == 0) return 0

        return Math.max(...allTimestamps)
    }

    private hasUserBeenPromptedRecentlyForSameIntent(): boolean {
        const timestampData = StorageWrapper.getRequestPushNotificationTimeData()
        const lastRequestPushNotificationTime = timestampData[this.popupType.intent]
        if(lastRequestPushNotificationTime == null) return false

        const frequency = this.popupType.frequency
        return new Date().valueOf() - new Date(lastRequestPushNotificationTime).valueOf() < frequency
    }

    private hasUserBeenPromptedRecently(): boolean {
        const latestPopupTimestamp = this.getLatestPopupTimestamp()
        if(latestPopupTimestamp == 0) return false

        const minimumGapBetweenAnyPopups = TimeUtils.MILLISECONDS * TimeUtils.SECONDS_IN_MINUTE * TimeUtils.MINUTES_IN_HOUR * TimeUtils.HOURS_IN_DAY
        return new Date().valueOf() - latestPopupTimestamp < minimumGapBetweenAnyPopups
    }

    static resetStorageTimeStamps() {
        const defaultPopupTimestamp = StorageWrapper.getLastRequestPushNotificationTime()
        if(!defaultPopupTimestamp) return

        const timestampData = StorageWrapper.getRequestPushNotificationTimeData()
        const newTimestampData = {
            [PushNotificationPopupIntentEnum.Default]: defaultPopupTimestamp,
            ...timestampData,
        }

        StorageWrapper.setRequestPushNotificationTimeData(newTimestampData)
    }
}
